import { I18n } from '@calendly/i18n';
import classNames from 'classnames';
import { nanoid } from 'nanoid';
import { useMemo, useRef, useState, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { timeNotationSelector } from 'selectors/current_user';
import { AriaAnnouncement } from './aria_announcement';
import { Container, Dash, DisabledWeekday, RemoveIntervalButton, TimeInputsContainer } from './styles';
import { TimeInput } from './time_input';
import { checkIntervalsSorted, generateSortedIntervalsAnnouncement, validateAndSortIntervals } from './utils';
const intervalContainerCss = "i3lbsch";
const IntervalContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(intervalContainerCss, props.className)} />;
});
const intervalErrorCss = "ino25vp";
const IntervalError = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'p';
  return <Component ref={ref} {...restProps} className={classNames(intervalErrorCss, props.className)} />;
});
const maxWidthIntervalErrorCss = "mpfvbpc";
const MaxWidthIntervalError = forwardRef((props, ref) => <IntervalError ref={ref} {...props} className={classNames(maxWidthIntervalErrorCss, props.className)} />);
const Interval = ({
  interval,
  updateInterval,
  removeInterval,
  autoFocus,
  index,
  day,
  v4Styling = false,
  showInlineRemove = true
}) => {
  const handleFromChanged = newFrom => updateInterval({
    ...interval,
    from: newFrom
  });
  const handleToChanged = newTo => updateInterval({
    ...interval,
    to: newTo
  });
  const dayNames = I18n.t('date.day_names_hash');
  const {
    from,
    to,
    error
  } = interval;
  const isInvalidFrom = ['overlap', 'invalid_time_from'].includes(error);
  const isInvalidTo = ['overlap', 'invalid_duration', 'invalid_time_to'].includes(error);
  const errorMessages = {
    invalid_duration: I18n.t('publisher.availability.schedules.interval_errors.invalid_duration'),
    overlap: I18n.t('publisher.availability.schedules.interval_errors.overlap'),
    invalid_time_from: I18n.t('publisher.availability.schedules.interval_errors.invalid_time'),
    invalid_time_to: I18n.t('publisher.availability.schedules.interval_errors.invalid_time')
  };
  const removeTitle = day ? I18n.t('publisher.availability.schedules.remove_day_interval_index', {
    day: dayNames[day],
    index: index + 1
  }) : I18n.t('publisher.availability.schedules.remove_interval_index', {
    index: index + 1
  });
  return <IntervalContainer data-component="interval">
      <TimeInputsContainer>
        <TimeInput autoFocus={autoFocus} value={from} onChange={handleFromChanged} aria-invalid={isInvalidFrom} aria-label={I18n.t('publisher.event_types.availability.from')} name="from" />
        <Dash />
        <TimeInput value={to} onChange={handleToChanged} aria-invalid={isInvalidTo} aria-label={I18n.t('publisher.event_types.availability.to')} name="to" />
        {showInlineRemove && <RemoveIntervalButton onClick={removeInterval} title={removeTitle} v4Styling={v4Styling} />}
      </TimeInputsContainer>
      {error && <MaxWidthIntervalError>{errorMessages[error]}</MaxWidthIntervalError>}
    </IntervalContainer>;
};
const EnabledWeekday = ({
  intervals,
  onChange,
  autoFocusLast,
  day,
  v4Styling = false,
  onRemoveSingleInterval
}) => {
  const [announcement, setAnnouncement] = useState('');
  const timeNotation = useSelector(timeNotationSelector);
  const updateInterval = newInterval => {
    const newIntervals = intervals.map(interval => interval.id === newInterval.id ? newInterval : interval);
    const validated = validateAndSortIntervals(newIntervals);
    onChange(validated);
    const intervalsHaveBeenSorted = checkIntervalsSorted(newIntervals, validated);
    if (intervalsHaveBeenSorted) {
      const sortedAnnouncement = generateSortedIntervalsAnnouncement(validated, timeNotation);
      setAnnouncement(`${I18n.t('publisher.event_types.availability.intervals_sorted')}: ${sortedAnnouncement}`);
    }
  };
  const removeInterval = id => {
    const newIntervals = intervals.filter(interval => interval.id !== id);
    const validated = validateAndSortIntervals(newIntervals);
    onChange(validated);
    const intervalsHaveBeenSorted = checkIntervalsSorted(newIntervals, validated);
    if (intervalsHaveBeenSorted) {
      const sortedAnnouncement = generateSortedIntervalsAnnouncement(validated, timeNotation);
      setAnnouncement(`${I18n.t('publisher.event_types.availability.intervals_sorted')}: ${sortedAnnouncement}`);
    }
  };
  return <div data-component="time-container">
      {intervals.map((interval, index) => <Interval key={interval.id} day={day} index={index} autoFocus={autoFocusLast && index === intervals.length - 1} interval={interval} updateInterval={updateInterval} removeInterval={() => v4Styling && intervals.length === 1 && onRemoveSingleInterval ? onRemoveSingleInterval() : removeInterval(interval.id)}
    // Always show remove for legacy and v4 (sole-interval v4 clears the day).
    showInlineRemove v4Styling={v4Styling} />)}
      <AriaAnnouncement announcement={announcement} />
    </div>;
};
const WdayRule = ({
  intervals,
  onChange,
  autoFocusLast,
  day,
  className,
  v4Styling = false,
  onRemoveSingleInterval
}) => {
  const idsCacheRef = useRef([]);
  const intervalsWithIds = useMemo(() => {
    const getIdFromCache = index => {
      if (!idsCacheRef.current[index]) {
        idsCacheRef.current.push(nanoid(4));
      }
      return idsCacheRef.current[index];
    };
    return intervals.map((interval, index) => ({
      ...interval,
      id: getIdFromCache(index)
    }));
  }, [intervals]);
  const cacheIdsAndChange = newIntervals => {
    idsCacheRef.current = newIntervals.map(({
      id
    }) => id);
    const normalizedIntervals = newIntervals.map(({
      from,
      to,
      error
    }) => {
      const interval = {
        from,
        to
      };
      if (error) interval.error = error;
      return interval;
    });
    onChange(normalizedIntervals);
  };
  return <Container className={className} v4Styling={v4Styling}>
      {intervals.length > 0 ? <EnabledWeekday day={day} intervals={intervalsWithIds} onChange={cacheIdsAndChange} autoFocusLast={autoFocusLast} v4Styling={v4Styling} onRemoveSingleInterval={onRemoveSingleInterval} /> : <DisabledWeekday v4Styling={v4Styling} />}
    </Container>;
};
export default WdayRule;

require("./index.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./index.js");